import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { FeaturedVideo } from '@/shared/types';
import { Button, ButtonLink, EmptyState, Spinner, ValoiVLockup } from '@/shared/ui';
import { StyleSection } from '@/features/landing/components/StyleSection';
import { buildStyleSections } from '@/features/landing/services/styleSections';
import { NowShowingHero } from '@/features/browse/components/NowShowingHero';
import { fetchShowcaseVideos } from '@/features/browse/services/showcaseService';
import {
  isSaved,
  listSavedContent,
  saveContent,
  unsaveContent,
  type SavedContent,
} from '@/features/browse/services/savedContentService';
import { useCurrentUser } from '@/app/providers/user';
import { useNotifications } from '@/app/providers/notifications';
import { LandingStage } from '@/features/landing/components/LandingStage';

/**
 * The public front door: what someone with no account sees at `/`.
 *
 * Deliberately built from the same three components the signed-in Home renders
 * (NowShowingHero, ContentRail, BrowseCard) rather than a marketing page beside
 * them. A visitor's whole question is "is the thing I would be signing up for any
 * good", and the honest answer is the real product surface with real videos
 * playing in it. A separately-styled sales page answers a different question, and
 * drifts from the app the moment either side changes.
 *
 * What differs from Home is only what needs an account. NowShowingHero and
 * BrowseCard both take an `onToggleSave` they will call, so this page hands them a
 * handler that routes to sign-in rather than pretending to save: an "added to
 * library" that vanishes on reload is worse than no button at all.
 */
/* How long an entry holds the hero before the reel moves on. Long enough to read
   the title and take in the still; short enough that the section is visibly alive.
   Deliberately unrelated to clip length -- a 6-second video and a 6-minute one
   should both get a fair look when nobody is watching either. */
const HERO_DWELL_MS = 7000;

export type LandingPageProps = {
  /**
   * Render the signed-in variant.
   *
   * The catalogue, the stage, the hero and the bands are identical -- this is one
   * page rendered twice, not two pages kept in sync. What a signed-in viewer gets
   * instead is: no Login button, a composer link in its place, different stage copy,
   * and no sign-in invitations on the save controls.
   *
   * Save is still inert here. Nothing on this page is bookmarkable yet because the
   * showcase feed carries no per-user state -- see the `onToggleSave` note below.
   */
  signedIn?: boolean;
};

export default function LandingPage({ signedIn = false }: LandingPageProps = {}) {
  const [videos, setVideos] = useState<FeaturedVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selected, setSelected] = useState<FeaturedVideo | null>(null);
  // Arriving is silent, exactly as on Home: nothing plays until the visitor picks
  // something. An autoplaying video on a first visit is what every marketing page
  // gets wrong.
  const [autoplay, setAutoplay] = useState(false);
  const heroRef = useRef<HTMLElement | null>(null);
  /* Mirrors `videos` for `advanceHero`, which must not re-create itself whenever
     the list is refetched. */
  const videosRef = useRef<FeaturedVideo[]>([]);
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { pushNotification } = useNotifications();
  const userId = user?.userId;
  /* What this viewer has already saved, so the bookmark renders filled rather than
     always empty. Signed out there is nobody to ask, and the list stays empty. */
  const [saved, setSaved] = useState<SavedContent[]>([]);
  /* Video ids with a save/unsave request outstanding. See `onSave`. */
  const inFlight = useRef<Set<string>>(new Set());
  /* Set when a write starts, cleared whenever the identity behind `saved` changes.
     While set, the writes own `saved` and the load must not publish over them. */
  const hasWritten = useRef(false);
  /* False once the component is gone, so an in-flight write does not publish into a
     tree that is no longer mounted. */
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /*
   * Every authenticated action on the reused components funnels here. Saving
   * needs an account, so the honest response to pressing it is to go and get
   * one -- not a toast, and not a control that silently does nothing.
   */
  const inviteSignIn = useCallback(() => navigate('/login'), [navigate]);

  /* Signed in, the press saves for real.

     It used to be a no-op. The reasoning was that `/home/showcase` is a public feed
     with no per-user rows behind it, so there was nothing to write -- but that was
     the wrong conclusion from a true premise. `saved-content` exists for exactly
     this: these are curated videos the account does not own, so it denormalises the
     card at save time rather than storing a reference that would 404 for the person
     who saved it. Browse has done this the whole time; this page simply was not
     wired to it.

     Same handler either way, because the control is the same control -- signed out
     it asks for an account, signed in it saves. */
  /* Load what is already saved, so a bookmark the viewer set earlier renders filled
     instead of empty. Signed out there is nobody to ask.

     Failure is swallowed: an unreadable saved-list is not a reason to break a page
     whose whole job is showing videos. The bookmarks render empty, and pressing one
     still writes -- the API returns the authoritative list, so the state corrects
     itself on the first press. */
  useEffect(() => {
    if (!signedIn || !userId) {
      setSaved([]);
      return;
    }
    /* Reset per identity. `hasWritten` means "a write has taken ownership of `saved`",
       and that ownership belongs to the viewer who did the writing -- it does not carry
       across an account switch. Left set, this effect's own GET for the NEW user would
       be blocked forever and they would keep seeing the previous user's bookmarks until
       they pressed one themselves. Cheap to reset, and it makes the flag mean what its
       name says rather than "any write since mount". */
    hasWritten.current = false;
    let cancelled = false;
    void listSavedContent(userId)
      .then((items) => {
        /* `hasWritten` as well as `cancelled`. These two are unordered: press a
           bookmark straight after load and the POST can resolve first, so this
           slower GET would then publish the pre-save list over it and the save
           would appear to revert until the next press. A write's response is
           always the fresher truth -- both return the same authoritative list --
           so once one has started, this result is stale by definition. */
        if (!cancelled && !hasWritten.current) setSaved(items);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [signedIn, userId]);

  const onSave = useCallback(
    (video: FeaturedVideo) => {
      if (!signedIn) {
        inviteSignIn();
        return;
      }
      if (!userId) return;
      /* One write per video at a time. `saved` is read from the closure, so without
         this a second press before the first resolves sees the same pre-toggle state:
         two presses both read "not saved" and both POST, or they race each other's
         `setSaved` and the final state reflects whichever request happened to land
         last rather than the last press. A ref rather than state -- the guard has to
         be readable synchronously within one press, and re-rendering for it would
         serve nothing. */
      if (inFlight.current.has(video.id)) return;
      inFlight.current.add(video.id);
      hasWritten.current = true;
      /* Whose write this is. Resetting `hasWritten` per identity concedes that this
         component can outlive an account switch, and a write already in flight for the
         previous viewer would otherwise publish their list over the new one's -- the
         same stale-overwrite this file already guards in the other direction. */
      const startedFor = userId;
      void (async () => {
        try {
          /* The list the API returns IS the new state, so there is no optimistic
             update to roll back: a failed write leaves the bookmark as it was, which
             is the honest picture. */
          const next = isSaved(saved, video.id)
            ? await unsaveContent(userId, video.id)
            : await saveContent(userId, video);
          /* Unmounted, or no longer this viewer's page: either way there is nothing
             this response should be published into. */
          if (mounted.current && startedFor === userId) setSaved(next);
        } catch {
          /* Said out loud, unlike the mount load. There is still no state to roll back
             -- the bookmark simply stays as it was -- but silence here is
             indistinguishable from the request being slow, so a viewer presses again
             and again with nothing to tell them it is not working. The load can afford
             to fail quietly because an empty bookmark still looks like a page; a press
             that appears to do nothing does not.

             Only when the page is still theirs, for the same reason `setSaved` is. */
          if (mounted.current && startedFor === userId) {
            pushNotification({ message: 'Could not update your library. Please try again.' });
          }
        } finally {
          inFlight.current.delete(video.id);
        }
      })();
    },
    [signedIn, inviteSignIn, userId, saved, pushNotification],
  );

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    void fetchShowcaseVideos()
      .then((list) => {
        setVideos(list);
        videosRef.current = list;
        setSelected(list[0] ?? null);
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  /* Choosing a tile promotes it to the hero and commits to playing it -- the
     visitor asked for this one, so it should not be walked past by the reel. */
  const handleSelect = useCallback((video: FeaturedVideo) => {
    setSelected(video);
    setAutoplay(true);
    setHeroIdle(false);
    heroRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  /* Grouped by how each video was rendered, not by curator category: `style` is
     set on every job by the pipeline, while `category` is empty until somebody
     curates it — so this is the only grouping that works on a fresh catalogue. */
  const bands = useMemo(() => buildStyleSections(videos), [videos]);

  /* Walks the hero to the next entry, so the section keeps introducing the
     catalogue instead of stopping on a last frame. */
  const advanceHero = useCallback(() => {
    /* Reads the list from a ref rather than closing over `videos`, so the handler
       identity stays stable across refetches and the <video> element does not get
       a new onEnded on every render. Nesting a setSelected inside a setVideos
       updater would have read the same value, but a state updater must be pure --
       React may call it twice in development, firing the advance twice. */
    const list = videosRef.current;
    if (list.length <= 1) return;
    setSelected((now) => {
      const at = list.findIndex((v) => v.id === now?.id);
      return list[(at + 1) % list.length] ?? now;
    });
  }, []);

  /* The reel's clock. `idle` means "nothing is playing in the hero", and while
     that holds the section walks to the next entry on a timer — the same
     never-stops-moving behaviour the wallpaper has, without needing a video to
     end first.
     
     Watching or hovering sets it false, which cancels the walk: the viewer is
     looking at THIS video and moving it out from under them would be hostile.
     Pausing or moving the pointer away sets it true again, and the delay below is
     the grace period before the reel picks back up — long enough that a pointer
     crossing the stage on its way somewhere else does not restart the cycle. */
  const [heroIdle, setHeroIdle] = useState(true);

  useEffect(() => {
    if (!heroIdle || videos.length <= 1) return;
    const id = window.setTimeout(advanceHero, HERO_DWELL_MS);
    return () => window.clearTimeout(id);
    /* `selected` is in the deps so the timer restarts per entry: without it the
       interval would keep firing against a stale closure and the reel would jump
       several videos at once after a pause. */
  }, [heroIdle, videos.length, selected?.id, advanceHero]);

  return (
    /* `.shell` (not `.shell--bare`): the same flex row the signed-in app uses, so
       the rail holds a real 264px column and the content flows beside it rather
       than under it. The stage is still edge-to-edge -- within the content
       column, which is what "full width" means once a rail exists. */
    /* No rail. The app's `Sidebar` was reused here so the furniture would not move
       when a visitor signs in, but every item in it was a destination they cannot
       reach yet -- and defaulting it open to keep the labels visible changed the rail
       for existing signed-in users who had never touched the toggle. A landing page
       does not need navigation it cannot honour: the mark goes top-left, the way in
       goes top-right, and the catalogue below is the whole rest of the page. */
    /* `page--bleed` only signed in, and only because of where the page sits then:
       inside AppLayout's content card, whose `.shell__content > *` rule imposes a prose
       measure and a gutter on every direct child. That boxed the full-bleed stage into
       a letterboxed rectangle floating in cream. Signed out there is no card, so the
       class would be describing a container that is not there. */
    <div className={signedIn ? 'landing page--bleed' : 'landing'}>
      {/* No top bar inside the rail shell. Signed in, this page renders under
          AppLayout, which already supplies the mark and the account menu in the left
          rail -- a second lockup two inches away reads as a nested site. Signed out
          there is no rail, so the bar is the only branding and the only way in. */}
      {signedIn ? null : (
        <header className="landing__bar">
          <div className="landing__bar-inner">
            {/* Home rather than /login: the mark is a way back to the top, and a logo
                that signs you up is a trap. The button beside it does the asking. */}
            <Link to="/" className="landing__brand" aria-label="Valoi — home">
              <ValoiVLockup title="Valoi" className="landing__logo" />
            </Link>
            {/* Signed out this is the way in. Signed in the viewer is already past it,
                so the slot goes to the thing they came to do instead of a button that
                would bounce them through a login form they do not need. */}
            {signedIn ? (
              <ButtonLink to="/" variant="primary">
                New content
              </ButtonLink>
            ) : (
              <ButtonLink to="/login" variant="primary">
                Login
              </ButtonLink>
          )}
        </div>
      </header>
      )}


      {loading ? (
        <div className="landing__loading">
          <Spinner size={26} label="Loading videos" />
        </div>
      ) : loadError ? (
        <div className="landing__state" role="alert">
          <h1 className="sr-only">Valoi</h1>
          <EmptyState
            headingLevel={2}
            title="Couldn&rsquo;t load videos"
            hint="Please check your connection and try again."
            action={
              <Button variant="secondary" onClick={load}>
                Try again
              </Button>
            }
          />
        </div>
      ) : videos.length === 0 ? (
        /* Nothing opted in to the showcase yet. Still a real page with a real
           invitation: a blank screen would read as broken to the one audience with
           no way to tell the difference. */
        <div className="landing__state">
          {/* Every state of this page needs a top-of-outline heading, and
              EmptyState caps at h2 by design (a page owns its h1). Hidden rather
              than drawn, because the empty state's own title is the visible one. */}
          <h1 className="sr-only">Valoi</h1>
          <EmptyState
            headingLevel={2}
            title="Watch what you read"
            hint="Novels, textbooks, your own writing — turned into video you can watch, annotate, and keep."
            action={
              <ButtonLink to="/login" variant="primary">
                Start Generating
              </ButtonLink>
            }
          />
        </div>
      ) : (
        <>
          {/* The whole showcase reel, not `selected`: the stage cycles through
              every entry on its own, and wiring it to the selection would make the
              wallpaper lurch under the headline every time a card was clicked. It
              is the poster for the catalogue, not a second player. */}
          <LandingStage videos={videos} signedIn={signedIn} />

          <div className="landing__column">
          {selected ? (
            <NowShowingHero
              video={selected}
              /* Signed out this is always false and pressing it invites sign-in;
                 signed in it reflects the viewer's library. */
              saved={isSaved(saved, selected.id)}
              onToggleSave={onSave}
              autoplay={autoplay}
              anchorRef={heroRef}
              /* Hovering is the browsing gesture on both surfaces. The note that used
                 to sit here said Home does not opt in, because the video there was
                 already the one the viewer had picked -- that was the OLD Home, a
                 player for a chosen video. This Home is the catalogue, so sweeping the
                 hero means the same thing signed in as signed out. */
              previewOnHover
              /* A clip reaching its end is the reel's natural cue: advance now
                 rather than after another dwell, so a finished video does not sit
                 on its last frame. */
              onEnded={() => {
                setHeroIdle(true);
                advanceHero();
              }}
              /* Playback stopping short -- Pause, or the pointer leaving a preview
                 -- hands the clock back, and the dwell timer starts again. */
              onPause={() => setHeroIdle(true)}
              onPlay={() => setHeroIdle(false)}
            />
          ) : null}

          {bands.length === 0 ? (
            <EmptyState
              headingLevel={2}
              title="Nothing to show yet"
              hint="Check back soon for new videos."
            />
          ) : (
            bands.map((band) => (
              <StyleSection
                key={band.key}
                section={band}
                selectedId={selected?.id}
                onSelect={handleSelect}
                onToggleSave={onSave}
                isSaved={(video) => isSaved(saved, video.id)}
                /* Client-side navigation, so the wall of playing videos is not torn
                   down and rebuilt -- a full page load here would restart every
                   clip and lose the hero's place. */
                onSeeAll={(band) => navigate(`/showcase/${band.key}`)}
              />
            ))
          )}

          <section className="landing__cta">
            <h2 className="landing__cta-title">
              {signedIn ? 'Make Something of Your Own' : 'Ready to Bring Your Text to Life?'}
            </h2>
            <p className="landing__lead">
              {signedIn
                ? 'Turn any prompt or multi-page storybook into a continuous video.'
                : 'Sign in to generate your first continuous video from any prompt or multi-page storybook.'}
            </p>
            {/* Same label as the stage's CTA. They are the same action and the same
                destination, and a visitor who scrolled past one and met a differently
                named button at the bottom had to work out whether it did something
                else. One name for one thing. */}
            <ButtonLink to={signedIn ? '/' : '/login'} variant="primary" size="lg">
              Start Generating
            </ButtonLink>
          </section>
          </div>
        </>
      )}
      </div>
  );
}
